import React, { useContext, useRef, useState, useMemo, useEffect } from 'react';
import { NextPage } from 'next';
import Link from 'next/link';

import classNames from 'classnames';
import { useRouter } from 'next/router';
import { i18n as I18n, withTranslation } from 'utils/i18n';
import { formatTextWithLinebreaks } from 'utils/display';
import { filteredSpaceDiscounts } from 'utils/filters';

import Page from 'components/page';
import SlideShow from 'components/shared/slideShow';
import FeatureIcon from 'components/shared/featureIcon';
import QuickBook from 'components/quickBook';
import AddToBookingButton from 'components/addToBookingButton';
import SpacePrice from 'components/spacePrice';
import BookingTimes from 'components/bookingTimes';
import FacetsEquipment from 'components/facetsEquipment';
import FloorplansButton from 'components/floorplansButton';
import NearbyVenues from 'components/nearbyVenues';
import DiscountTermsModal from 'components/discountTermsModal';

import { useApiRequest } from 'hooks/useApiRequest';
import RelatedSpaces from 'components/relatedSpaces';
import useStoredBooking from 'hooks/useStoredBooking';
import useDataLayer from 'hooks/useDataLayer';
import { AuthContext } from 'state/auth';
import useNearbyVenues from 'hooks/useNearbyVenues';
import { fixVenue } from 'hooks/useVenue';
import Discount from 'components/discount';

import { getDiscounts } from 'hooks/usePropertyDiscount';
import { maxBy } from 'lodash';

interface Facet {
  name: string;
  icon: string;
  iconAltText: string;
  isKey: boolean;
}
interface Space {
  id: number;
  name: string;
  clinical: boolean;
  images: {
    regular: string[];
    thumbnail: string[];
  };
  standardPrice: number;
  priceIncVat: number;
  capacity: number;
  description: string;
  notes: string;
  facets: Facet[];
  venue?: Venue;
  openingTimes: [];
  equipment: Equipment[];
  floor: string;
  discounts: any;
}

interface Equipment {
  name: string;
}

interface LatLng {
  lat: number;
  lng: number;
}

interface BookingSlot {
  startTime: string;
  endTime: string;
  name: string;
  daysOfWeek: string[];
}

interface Venue {
  id: number;
  name: string;
  latLng: LatLng;
  facets: Facet[];
  address: Address;
  bookingSlots: BookingSlot[];
}

interface Address {
  city: string;
  postcode: string;
}

const RoomPage: NextPage = () => {
  const router = useRouter();
  const [space, setSpace] = useState<Space>(undefined);
  const [discountTermsModalIsOpen, setDiscountTermsModalIsOpen] = useState(
    false
  );
  const { id } = router.query;

  const { loggedIn } = useContext(AuthContext);

  const apiRequest = useApiRequest();

  const quickBookRef = useRef({ scrollIntoView: () => null });

  const [
    { spaceIds, venueId: storedVenueId },
    setStoredBooking,
  ] = useStoredBooking();

  const addedToBooking = useMemo(() => spaceIds.includes(Number(id)), [
    spaceIds,
    id,
  ]);

  const canAddToBooking = useMemo(
    () => spaceIds.length === 0 || (space && storedVenueId === space.venue.id),
    [spaceIds, storedVenueId, space]
  );

  const selectSpace = (space, venue) => {
    if (canAddToBooking && !addedToBooking) {
      setStoredBooking(storedBooking => ({
        ...storedBooking,
        venueId: venue.id,
        spaceIds: [...spaceIds, space.id],
      }));
    }

    quickBookRef.current.scrollIntoView({ behaviour: 'smooth' });
    // adding as .scrollIntoView above doesn't scroll enough due to sticky header
    window.scrollBy(0, -120);
  };

  useEffect(() => {
    const fetchSpace = async () => {
      try {
        const { data = { venue: {} } } = await apiRequest.get(
          `/v1/spaces/${id}`
        );
        const discounts = await getDiscounts(apiRequest, {
          venueIds: [data.venue.id],
          spaceIds: [Number(id)],
          roomTypeIds: data.categoryIds,
        });

        const filteredDiscounts = filteredSpaceDiscounts(
          discounts,
          data.venue,
          data
        );

        setSpace({
          ...data,
          discount: maxBy(filteredDiscounts, discount => discount.amount),
          discounts: filteredDiscounts,
          venue: fixVenue(data.venue),
        });
      } catch (err) {
        router.push('/404');
      }
    };

    fetchSpace();
  }, [id, apiRequest, router, loggedIn]);

  const type = space && space.clinical ? 'Clinical' : 'Non-Clinical';

  const dataLayer =
    space && space.id
      ? {
          event: 'ospace.room',
          location: space.venue.address ? space.venue.address.city : null,
          roomType: type,
          venueId: space.venue.id,
          venueName: space.venue.name,
          roomId: space.id,
          roomName: space.name,
          roomValue: space.priceIncVat,
        }
      : null;

  useDataLayer(dataLayer);

  const { venue = { id: undefined } } = {
    ...(!space ? {} : space),
  };

  const { data: venues = [] } = useNearbyVenues({
    venueId: venue.id,
  });

  if (!space) {
    return null;
  }

  const headerClasses = classNames({
    'rooms--clinical': !!space.clinical,
    'rooms--non-clinical': !space.clinical,
  });

  /* eslint-disable camelcase */
  const blocksClasses = classNames({
    rooms__block: true,
    'rooms__block--clinical': !!space.clinical,
    'rooms__block--non-clinical': !space.clinical,
  });
  /* eslint-enable camelcase */

  return (
    <Page
      title={I18n.t('headers.room_page.title', {
        roomName: space.name,
        propertyName: space.venue.name,
      })}
      description={space.description}
    >
      <main className="rooms">
        <section>
          <div className="section-content">
            <div className="rooms__heading">
              <div className="rooms__heading-titles">
                <h4 className={headerClasses}>{type}</h4>
                <h1>{space.name}</h1>
                <Link href={`/properties/${space.venue.id}`}>
                  <a href={`/properties/${space.venue.id}`}>
                    {space.venue.name}
                  </a>
                </Link>
              </div>
              <div className="rooms__price" data-cy="price">
                <SpacePrice price={space.priceIncVat} />
              </div>
            </div>

            <div className="rooms__content layout layout--two-col-stacked-left">
              <div className="layout__first">
                <div className={blocksClasses}>
                  <div className="rooms__slideshow" id="slideShow">
                    {space.discounts.length > 0 && (
                      <>
                        <Discount
                          text={I18n.t('discounts.save_up_to', {
                            percent: space.discount.amount,
                          })}
                          rotated
                          size="large"
                          onClick={() => setDiscountTermsModalIsOpen(true)}
                        />

                        <DiscountTermsModal
                          isOpen={discountTermsModalIsOpen}
                          onClose={() => setDiscountTermsModalIsOpen(false)}
                          propertyOrRoomName={space.name}
                          discounts={space.discounts}
                        />
                      </>
                    )}
                    <SlideShow
                      slideUrls={space.images.regular}
                      mapLatLng={space.venue.latLng}
                    />

                    {!space.clinical && space.capacity > 0 && (
                      <a
                        data-testid="spaceCapacity"
                        className="venue__space-capacity"
                      >
                        <FeatureIcon
                          type="capacity"
                          name={I18n.t('feature_icon.room_capacity', {
                            num: space.capacity,
                          })}
                          additionalText={String(space.capacity)}
                          displaySvgIcon
                        />
                      </a>
                    )}
                    <FloorplansButton venue={space.venue} space={space} />
                  </div>

                  <div className="rooms__summary">
                    {space.description &&
                      formatTextWithLinebreaks(space.description)}
                  </div>
                  <BookingTimes
                    bookingSlots={space.venue.bookingSlots}
                    openingTimes={space.openingTimes}
                    clinicalRooms={type === 'Clinical'}
                    nonClinicalRooms={type === 'Non-Clinical'}
                    text={I18n.t('rooms.booking_times_text')}
                    collapsible={false}
                  />
                  <FacetsEquipment
                    roomFacets={space.facets}
                    propertyFacets={space.venue.facets}
                    roomEquipment={space.equipment}
                    floor={space.floor}
                    notes={space.notes}
                  />
                </div>
              </div>

              <div className="layout__last rooms__sidebar">
                {loggedIn && (
                  <AddToBookingButton
                    className="button--block"
                    onClick={() => selectSpace(space, space.venue)}
                    added={addedToBooking}
                    enabled={canAddToBooking}
                    desktop
                  />
                )}

                {loggedIn && <QuickBook ref={quickBookRef} />}

                <RelatedSpaces
                  space={space}
                  disabledSpaceIds={spaceIds}
                  selectSpace={canAddToBooking ? selectSpace : undefined}
                />
              </div>
            </div>
          </div>
        </section>

        {venue && venues.length > 0 && venue.address && (
          <NearbyVenues location={venue.address.postcode} venues={venues} />
        )}
      </main>
    </Page>
  );
};

RoomPage.getInitialProps = () => {
  return {
    namespacesRequired: ['common'],
  };
};

export default withTranslation('common')(RoomPage);
